import { useCallback, useEffect } from "react";
import { get as lodashGet } from "lodash-es";
import { languageConstants } from "main/src/js/constants";
import type { OptionItem } from "@certa/types";
import { renderLanguageNativeName } from "main/src/js/utils/languageHelpers";
import { FormattedMessage, useIntl } from "react-intl";
import { useGetSupportedLanguages } from "../../../hooks/useGetSupportedLanguages";
import { Godaam } from "../../../utils/Godaam";
import { removeBracketsAndAppendDash } from "../../../utils/helper";
import { useLanguageStore } from "../../../store/language/useLanguageStore";
import { useInstanceConfigQuery } from "@certa/queries/hooks/config.hook";
import type { InstanceConfiguration } from "@certa/queries/types/config.types";

/**
 * Custom hook for managing language-related functionality.
 *
 * @returns An object containing the following properties:
 *   - supportedLanguages: An array of supported languages.
 *   - preferredLanguage: The selected language for translations.
 *   - renderLanguageMenuItem: A function that renders the language menu item.
 *   - renderLanguageMenuTitle: A function that renders the language menu title.
 *   - handleLanguageChange: A function that handles language change on select.
 */
const useLanguages = () => {
  const supportedLanguages = useGetSupportedLanguages();
  const { data: config } = useInstanceConfigQuery();
  const _supportedLanguagesJson = lodashGet(
    config,
    "supported_languages_json",
    {} as InstanceConfiguration["supported_languages_json"]
  );

  const preferredLanguage = useLanguageStore(state => state.language);
  const setLanguage = useLanguageStore(state => state.setLanguage);
  const updateUserLanguage = useLanguageStore(
    state => state.updateUserLanguage
  );

  useEffect(() => {
    if (!Godaam.preferredLanguage) {
      setLanguage(languageConstants.DEFAULT_LOCALE);
    }
  }, [setLanguage]);

  const renderLanguageMenuTitle = () => {
    return (
      <span style={{ whiteSpace: "nowrap" }}>
        <FormattedMessage id={"loginPageInstances.languageText"} />:{" "}
        <span style={{ textTransform: "uppercase" }}>{preferredLanguage}</span>
      </span>
    );
  };

  const handleLanguageChange = useCallback(
    (value: string) => {
      updateUserLanguage(value);
    },
    [updateUserLanguage]
  );
  const intl = useIntl();

  const renderLanguageMenuItem = (language: OptionItem) => {
    const translatedLanguage = intl.formatMessage({
      id: `language.${_supportedLanguagesJson[language.value]}`,
      defaultMessage: _supportedLanguagesJson[language.value]
    });
    const nativeLanguageWithBrackets = renderLanguageNativeName(language.value);
    const formattedTranslatedLanguage =
      removeBracketsAndAppendDash(translatedLanguage);
    if (nativeLanguageWithBrackets) {
      const nativeLanguage = removeBracketsAndAppendDash(
        nativeLanguageWithBrackets
      );
      return `${formattedTranslatedLanguage} (${nativeLanguage} - ${language.value})`;
    }
    return `${formattedTranslatedLanguage} (${language.value})`;
  };

  return {
    supportedLanguages,
    preferredLanguage,
    renderLanguageMenuItem,
    renderLanguageMenuTitle,
    handleLanguageChange
  };
};

export { useLanguages };
